import { ArrowRight, ShieldCheck, Wrench, RotateCcw, BatteryCharging, Activity, AlertTriangle, Calendar, FileText } from "lucide-react";
import { AED_IMAGE } from "@/lib/data";
import SEO from "@/components/SEO";
import QuoteForm from "@/components/QuoteForm";
import HeartbeatLine from "@/components/HeartbeatLine";

const RENTAL_INCLUDES = [
    { icon: RotateCcw, title: "Installazione", text: "Consegna, fissaggio a parete con teca e segnaletica inclusa." },
    { icon: ShieldCheck, title: "Formazione base", text: "Sessione di istruzione all'uso per il personale del cliente." },
    { icon: Wrench, title: "Manutenzione programmata", text: "Audit annuale, test funzionali e documentazione conforme." },
    { icon: BatteryCharging, title: "Consumabili inclusi", text: "Sostituzione di elettrodi (adulto/pediatrici) e batterie a fine vita." },
    { icon: Calendar, title: "Contratti flessibili", text: "Mensili o annuali. Estensibili. Possibilità di sostituzione dispositivo." },
    { icon: FileText, title: "Documentazione", text: "Registro di manutenzione e certificazioni sempre disponibili." },
];

const AED_SECTORS = ["Uffici", "Fabbriche", "Scuole", "Palestre", "Hotel", "Centri commerciali", "Cantieri", "Comuni", "Eventi sportivi"];

export default function Defibrillatore() {
    return (
        <>
            <SEO
                title="Defibrillatori AED a Noleggio in Ticino · 7MED"
                description="Noleggio defibrillatori AED in Ticino: canone mensile o annuale, installazione, formazione base, manutenzione programmata e sostituzione consumabili inclusi. Zero investimento iniziale. Aziende, palestre, scuole, hotel."
                path="/defibrillatori-noleggio"
                keywords="noleggio defibrillatore AED Ticino, AED noleggio Svizzera, manutenzione defibrillatore, defibrillatore aziendale, AED palestra scuola, contratto noleggio defibrillatore"
                ogImage={AED_IMAGE}
                structuredData={{
                    "@context": "https://schema.org",
                    "@type": "Service",
                    serviceType: "Noleggio e manutenzione defibrillatori AED",
                    provider: { "@id": "https://7med.ch/#organization" },
                    areaServed: { "@type": "AdministrativeArea", name: "Ticino" },
                    description: "Noleggio AED con manutenzione, installazione, formazione base, sostituzione consumabili e contratti flessibili mensili o annuali.",
                    offers: {
                        "@type": "Offer",
                        priceSpecification: { "@type": "PriceSpecification", priceCurrency: "CHF", description: "Canone mensile o annuale" },
                        availability: "https://schema.org/InStock",
                    },
                }}
                breadcrumbs={[{ name: "Home", path: "/" }, { name: "Defibrillatori", path: "/defibrillatori-noleggio" }]}
            />
            {/* HERO */}
            <section className="bg-ink text-white">
                <div className="container-7m py-12 sm:py-16 lg:py-24">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
                        <div className="lg:col-span-7">
                            <div className="label-eyebrow !text-brand">Defibrillatori · Noleggio e Manutenzione</div>
                            <h1 className="mt-4 font-heading font-black text-4xl sm:text-5xl lg:text-7xl tracking-tighter leading-[1.05]">
                                Un AED.<br /><span className="text-brand">Senza doverlo comprare.</span>
                            </h1>
                            <p className="mt-6 sm:mt-8 text-base sm:text-lg lg:text-xl text-white/85 max-w-2xl leading-relaxed">
                                Noleggio di defibrillatori automatici certificati con <strong className="text-white">manutenzione inclusa</strong>.
                                Canone mensile o annuale, installazione, formazione base, sostituzione consumabili. Zero investimento iniziale.
                            </p>
                            <div className="mt-10 flex gap-4 flex-wrap">
                                <a href="#preventivo" className="btn-primary" data-testid="aed-hero-cta">Richiedi preventivo <ArrowRight className="w-4 h-4" /></a>
                                <a href="tel:+41912615199" className="btn-secondary !border-white !text-white hover:!bg-white hover:!text-ink">Chiama 091 261 51 99</a>
                            </div>
                        </div>
                        <div className="lg:col-span-5">
                            <div className="aspect-square overflow-hidden">
                                <img src={AED_IMAGE} alt="Defibrillatore AED 7MED in noleggio" className="w-full h-full object-cover" loading="eager" />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="opacity-20"><HeartbeatLine height={40} /></div>
            </section>

            {/* PERCHÉ NOLEGGIO */}
            <section className="py-20 lg:py-28 bg-white">
                <div className="container-7m grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <div className="label-eyebrow text-brand">Perché noleggiare</div>
                        <h2 className="mt-4 font-heading font-black text-4xl lg:text-5xl tracking-tighter leading-tight">Più flessibilità.<br />Meno pensieri.</h2>
                    </div>
                    <div className="lg:col-span-7 space-y-4">
                        <Benefit title="Zero investimento iniziale" text="Un AED professionale può costare migliaia di franchi. Con il noleggio ottieni subito un dispositivo di qualità con un canone contenuto." />
                        <Benefit title="Manutenzione inclusa per sempre" text="Audit annuali, test funzionali, batterie ed elettrodi: ce ne occupiamo noi. Tu hai solo da esporre correttamente il dispositivo." />
                        <Benefit title="Sempre aggiornato" text="A fine ciclo di vita il dispositivo viene sostituito. Il tuo AED è sempre conforme ai protocolli più recenti." />
                        <Benefit title="Conformità normativa" text="Documentazione e registro di manutenzione pronti in caso di audit o ispezioni." />
                    </div>
                </div>
            </section>

            {/* COSA È INCLUSO */}
            <section className="py-20 lg:py-28 bg-[#FAFAFA] border-y border-ink/10">
                <div className="container-7m">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14">
                        <div className="lg:col-span-5">
                            <div className="label-eyebrow text-brand">Cosa è incluso</div>
                            <h2 className="mt-4 font-heading font-black text-4xl lg:text-6xl tracking-tighter leading-none">Tutto compreso.</h2>
                        </div>
                        <p className="lg:col-span-6 lg:col-start-7 text-ink-muted leading-relaxed">
                            Un unico canone per un servizio completo. Niente sorprese, niente costi nascosti: dal primo giorno il dispositivo è operativo e lo mantieni attivo senza pensieri.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-ink/10">
                        {RENTAL_INCLUDES.map((s, i) => (
                            <div key={s.title} className="bg-white p-8 hover:bg-ink hover:text-white transition-colors duration-300 group" data-testid={`aed-include-${i}`}>
                                <div className="flex items-start justify-between">
                                    <s.icon className="w-7 h-7 text-brand" strokeWidth={1.5} />
                                    <span className="font-mono text-xs text-ink-muted group-hover:text-white/40">0{i + 1}</span>
                                </div>
                                <h3 className="mt-6 font-heading font-bold text-xl tracking-tight">{s.title}</h3>
                                <p className="mt-3 text-sm text-ink-muted group-hover:text-white/70 leading-relaxed">{s.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* DOVE SERVE */}
            <section className="py-20 lg:py-28 bg-white">
                <div className="container-7m grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <AlertTriangle className="w-10 h-10 text-brand" strokeWidth={1.5} />
                        <h2 className="mt-6 font-heading font-black text-4xl lg:text-5xl tracking-tighter leading-tight">Dove serve un AED?</h2>
                        <p className="mt-6 text-ink-muted">Ovunque ci siano persone. La defibrillazione precoce è il più potente strumento per aumentare la sopravvivenza in caso di arresto cardiaco improvviso.</p>
                    </div>
                    <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-px bg-ink/10">
                        {AED_SECTORS.map((p) => (<div key={p} className="bg-white p-6 text-center font-heading font-bold text-lg">{p}</div>))}
                    </div>
                </div>
            </section>

            {/* STATS */}
            <section className="py-16 bg-brand text-white">
                <div className="container-7m grid grid-cols-1 md:grid-cols-3 gap-8 text-center md:text-left">
                    <StatHero big="90%" label="Probabilità di morte entro 10 minuti se l'AED non interviene" />
                    <StatHero big="5 min" label="Tempo critico per un intervento efficace" />
                    <StatHero big="+70%" label="Sopravvivenza con AED a portata di mano" />
                </div>
            </section>

            {/* FORM */}
            <section id="preventivo" className="py-20 lg:py-28 bg-white">
                <div className="container-7m grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <div className="label-eyebrow text-brand">Preventivo noleggio</div>
                        <h2 className="mt-4 font-heading font-black text-4xl lg:text-5xl tracking-tighter leading-tight">Parliamone.</h2>
                        <p className="mt-6 text-ink-muted">Un'analisi gratuita del tuo spazio, una proposta su misura. Risposta entro 24 ore.</p>
                        <div className="mt-8 flex items-center gap-3 text-sm">
                            <ShieldCheck className="w-5 h-5 text-brand" />
                            <span>Consulenza gratuita e senza impegno</span>
                        </div>
                    </div>
                    <div className="lg:col-span-7 bg-[#FAFAFA] p-8 lg:p-12 border border-ink/10">
                        <QuoteForm defaultCourse="Defibrillatore — noleggio/manutenzione" />
                    </div>
                </div>
            </section>
        </>
    );
}

function Benefit({ title, text }) {
    return (
        <div className="border-l-2 border-brand pl-6 py-2">
            <h3 className="font-heading font-bold text-xl">{title}</h3>
            <p className="mt-2 text-sm text-ink-muted leading-relaxed">{text}</p>
        </div>
    );
}

function StatHero({ big, label }) {
    return (
        <div>
            <Activity className="w-7 h-7 text-white mx-auto md:mx-0" strokeWidth={1.5} />
            <div className="font-heading font-black text-5xl lg:text-6xl mt-4 tracking-tighter">{big}</div>
            <div className="text-sm text-white/80 mt-2 max-w-xs">{label}</div>
        </div>
    );
}
